<template>
    <div class="container container-upgradeAcct acct2" :class="{ 'is-dark': showMode }" v-if="ready" :key="handler" id="upgradeAcct">
        <div class="plan-update-overlay" v-if="planUpdatePending">
            <div class="plan-update-card">
                <div class="plan-update-spinner" aria-hidden="true"></div>
                <div class="plan-update-title">Updating your subscription…</div>
                <p class="plan-update-subtitle" v-if="!planUpdateSlow">
                    Hang tight — we're confirming your payment with Stripe. This usually takes just a few seconds.
                </p>
                <p class="plan-update-subtitle" v-else>
                    Still finishing up on Stripe's side — this can occasionally take a little longer. We'll update this automatically as soon as it's done.
                </p>
                <button
                    type="button"
                    class="plan-update-dismiss"
                    v-if="planUpdateSlow"
                    @click="clearPendingPlanUpdate">
                    Continue to my account
                </button>
            </div>
        </div>

        <!-- App header: menu + dark/light toggle (left), Cuelist logo (right, back to dashboard). -->
        <header class="acct-topbar">
            <div class="tb-left">
                <button class="tb-icon" @click="openNav" aria-label="Menu">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M4 7h16M4 12h16M4 17h16"/></svg>
                </button>
                <button class="theme-toggle" @click="toggleShowMode" :aria-label="showMode ? 'Switch to light mode' : 'Switch to dark mode'">
                    <span class="ti">{{ showMode ? '☀' : '☾' }}</span><span>{{ showMode ? 'Light' : 'Dark' }}</span>
                </button>
            </div>
            <div class="tb-right">
                <a class="brand-logo-link" href="#" @click.prevent="goDashboard" title="Back to dashboard">
                    <img class="brand-logo" :src="require('../assets/img/cuelist-full.svg')" alt="Cuelist" />
                </a>
            </div>
        </header>


        <core-nav-main :user="user"></core-nav-main>

        <!-- Section tabs -->
        <nav class="acct-tabs" aria-label="Account sections">
            <button class="acct-tab" :class="{ 'is-active': activeTab === 'profile' }" @click="activeTab = 'profile'">My Profile</button>
            <button class="acct-tab" :class="{ 'is-active': activeTab === 'sessions' }" @click="activeTab = 'sessions'">User Sessions</button>
        </nav>

        <main class="acct-content">

            <!-- ============ MY PROFILE ============ -->
            <section class="acct-panel" v-show="activeTab === 'profile'">

                <div class="acct-grid">

                    <!-- Identity rail -->
                    <aside class="rail card">
                        <div class="avatar-wrap">
                            <div class="avatar">
                                <img v-if="avatarUrl" :src="avatarUrl" alt="Profile photo" />
                                <img v-else :src="require('../assets/img/silhouette.jpg')" alt="Profile photo" />
                            </div>
                            <button v-if="isEmailUser" class="avatar-edit" @click="openDialog" title="Upload a new photo">✎</button>
                        </div>
                        <profile-photo ref="imageUpload"></profile-photo>

                        <!-- Name -->
                        <form v-if="editingName" class="rail-edit" novalidate @submit.stop.prevent="saveName">
                            <input class="rail-input" v-model="new_username" placeholder="Name" aria-label="Name" />
                            <div class="rail-edit-actions">
                                <button type="button" class="btn btn-ghost" @click="cancelEditingName">Cancel</button>
                                <button type="submit" class="btn btn-primary">Save</button>
                            </div>
                        </form>
                        <h1 v-else class="rail-name" :class="{ editable: isEmailUser }" @click="editName">{{ user.username }}</h1>

                        <!-- Email -->
                        <form v-if="editingEmail" class="rail-edit" novalidate @submit.stop.prevent="saveEmail">
                            <input class="rail-input" v-model="new_email" placeholder="Email" aria-label="Email" />
                            <div class="rail-edit-actions">
                                <button type="button" class="btn btn-ghost" @click="cancelEditingEmail">Cancel</button>
                                <button type="submit" class="btn btn-primary">Save</button>
                            </div>
                        </form>
                        <p v-else class="rail-email" :class="{ editable: isEmailUser }" @click="editEmail">{{ user.email }}</p>

                        <!-- Provider note -->
                        <p v-if="isEmailUser" class="rail-sync">Click your name, email, or photo to edit</p>
                        <template v-else>
                            <p class="rail-sync">Synced from your Google account</p>
                            <a class="rail-manage" href="https://myaccount.google.com/personal-info" target="_blank" rel="noopener">Manage in Google ↗</a>
                        </template>

                        <!-- Status -->
                        <div class="status-pill"><span class="dot"></span> {{ isPaidCustomer ? 'Collaborator' : 'Observer' }}</div>

                        <!-- Institution (educational / institutional admins) -->
                        <div class="rail-block" v-if="isInstitutionAdmin">
                            <div class="rail-div"></div>
                            <p class="rail-block-label">Institution</p>
                            <p class="rail-block-value">{{ user.institution_name }}</p>
                            <p class="rail-role">{{ user.subscription_level === 'educational' ? 'Educational Subscription Administrator' : 'Institutional Subscription Administrator' }}</p>
                        </div>

                        <!-- Change password (email users only) -->
                        <div class="rail-block" v-if="isEmailUser">
                            <div class="rail-div"></div>
                            <button class="rail-security" @click="openPasswordModal"><span class="lock">🔒</span> Change password</button>
                        </div>
                    </aside>

                    <!-- Content column -->
                    <div class="col">

                        <section class="card">
                            <p class="card-eyebrow">
                                <!-- Renewal cycle. Deliberately the opposite of the Show Pass
                                     ticket below: a subscription recurs, a pass is one-time, and the
                                     two marks now encode that difference rather than decorating
                                     two headings. Replaced a typographic diamond. -->
                                <svg class="glyph" width="15" height="15" viewBox="0 0 24 24" fill="none"
                                     stroke="currentColor" stroke-width="1.9" stroke-linecap="round"
                                     stroke-linejoin="round" aria-hidden="true">
                                    <path d="M20 12A8 8 0 1 1 12 4"/>
                                    <path d="M9 1 12 4 9 7"/>
                                </svg>
                                Subscription
                            </p>

                            <!-- Institution admin -->
                            <template v-if="isInstitutionAdmin">
                                <div class="plan-row">
                                    <h2 class="plan-name">{{ user.subscription_level === 'educational' ? 'Educational' : 'Institutional' }}</h2>
                                    <span class="plan-badge">Administrator</span>
                                </div>
                                <p class="plan-sub">You manage seats and billing for {{ user.institution_name }}.<template v-if="cancellingCard"><br>Your <strong>{{ cancellingCard.planName }}</strong> subscription. {{ cancellingCard.endsLine }}</template></p>
                                <div class="btn-row">
                                    <button class="btn btn-primary" @click="switchPlan">Manage Plan</button>
                                    <button class="btn btn-ghost" @click="manageActiveUsers">Manage Active Users</button>
                                </div>
                            </template>

                            <!-- Individual subscriber -->
                            <template v-else-if="user.active_pay">
                                <div class="plan-row">
                                    <h2 class="plan-name">Collaborator</h2>
                                    <span class="plan-badge">Active</span>
                                </div>
                                <template v-if="user.affiliateInstitution">
                                    <p class="plan-sub">Affiliated with <strong>{{ affiliateInstitutionLabel }}</strong>.</p>
                                    <div class="btn-row">
                                        <button class="btn btn-ghost" @click="affiliateInstitution">Update Affiliation</button>
                                    </div>
                                </template>
                                <template v-else>
                                    <p class="plan-sub" v-if="cancellingCard">Your <strong>{{ cancellingCard.planName }}</strong> subscription. {{ cancellingCard.endsLine }}</p>
                                    <p class="plan-sub" v-else-if="studentCard">Your <strong>{{ studentCard.planName }}</strong> subscription. {{ studentCard.endsLine }}</p>
                                    <p class="plan-sub" v-else>Your personal Cuelist subscription.</p>
                                    <div class="btn-row">
                                        <button class="btn btn-primary" @click="switchPlan">Manage Plan</button>
                                    </div>
                                </template>
                            </template>

                            <!-- Non-subscriber. Two states, not one: a user who has
                                 affiliated with an institution that has not yet activated
                                 their license is still an Observer, but saying nothing
                                 about it made the card read as "no affiliation set" and
                                 offered "Add Affiliation" for one they already have. -->
                            <template v-else>
                                <div class="plan-row">
                                    <h2 class="plan-name">Observer</h2>
                                    <span class="plan-badge muted">Free</span>
                                </div>
                                <template v-if="affiliationPending">
                                    <p class="plan-sub">
                                        Affiliated with <strong>{{ affiliateInstitutionLabel }}</strong> &mdash;
                                        <span class="plan-pending">awaiting activation</span>.
                                    </p>
                                    <div class="btn-row">
                                        <button class="btn btn-primary" @click="upgradeSubscription">Upgrade Account</button>
                                        <button class="btn btn-ghost" @click="affiliateInstitution">Update Affiliation</button>
                                    </div>
                                </template>
                                <template v-else>
                                    <p class="plan-sub">Upgrade to a Collaborator subscription to create and edit projects.</p>
                                    <div class="btn-row">
                                        <button class="btn btn-primary" @click="upgradeSubscription">Upgrade Account</button>
                                        <button class="btn btn-ghost" @click="affiliateInstitution">Add Affiliation</button>
                                    </div>
                                </template>
                            </template>

                            <!-- Institution invitations, at the bottom of the Subscription
                                 card (Josh, 3 Sept). Outside the three plan branches on
                                 purpose: an invitation can arrive for an Observer, an
                                 individual subscriber or an institution admin, and it reads
                                 the same in all three. The component hides itself when there
                                 is nothing pending, so this adds no height in the normal
                                 case. variant="card" drops the dashboard's grey panel and
                                 draws from the .acct2 variables instead. -->
                            <pending-affiliations
                                variant="card"
                                :user="user"
                                :pendingReady="affiliateInvitesReady"
                                @affiliationAccepted="handleAffiliationAccepted"
                            />
                        </section>

                        <!-- Show Pass -->
                        <section class="card pass-card" v-if="showPassPriceId">
                            <div class="pass-head">
                                <div>
                                    <p class="card-eyebrow">
                                        <!-- Same ticket as the dashboard banner, at 15px. Both stroke
                                             weights step up together (1.6->1.9, 1.3->1.55) because stroke
                                             width does not scale with the box: held at the banner values
                                             the mark would go spindly at this size. -->
                                        <svg class="glyph" width="15" height="15" viewBox="0 0 24 24" fill="none"
                                             stroke="currentColor" stroke-linejoin="round" stroke-linecap="round"
                                             aria-hidden="true">
                                            <path stroke-width="1.9" d="M4 5H20A2 2 0 0 1 22 7V9.5A2.5 2.5 0 0 0 22 14.5V17A2 2 0 0 1 20 19H4A2 2 0 0 1 2 17V14.5A2.5 2.5 0 0 0 2 9.5V7A2 2 0 0 1 4 5Z"/>
                                            <path stroke-width="1.55" stroke-dasharray="1.4 1.8" d="M15.6 6.4V17.6"/>
                                        </svg>
                                        Show Pass
                                    </p>
                                    <h2 class="pass-title">Cover a whole production, one time</h2>
                                </div>
                                <div class="price-chip"><span class="amt">$299</span><span class="per">one-time</span></div>
                            </div>
                            <p class="pass-copy">A Show Pass unlocks <strong>one project for your entire team to edit</strong> — no seat limit, and <strong>no change to your subscription</strong>.</p>
                            <div class="btn-row">
                                <form method="POST" :action="checkoutUrl">
                                    <input type="hidden" name="mode" value="payment" />
                                    <input type="hidden" name="product" :value="showPassPriceId" />
                                    <input type="hidden" name="user_id" :value="user.uid" />
                                    <input type="hidden" name="email_id" :value="user.email" />
                                    <input type="hidden" name="customer_id" :value="user.stripe_id" />
                                    <button type="submit" class="btn btn-primary">Buy a Show Pass</button>
                                </form>
                            </div>
                        </section>

                    </div>
                </div>

                <!-- Email preferences — full-width band, ABOVE the Notes List band.
                     Placement decided 8 Sept: this is a Cuelist setting and the
                     Notes List band is a third-party integration, so our own
                     settings group first. It also sits above a CONDITIONAL
                     section (the NL band renders only when the integration flag
                     is on), which keeps this block in the same place for every
                     user rather than moving depending on a flag they cannot see. -->
                <section class="card email-prefs">
                    <p class="card-eyebrow">
                        <svg class="glyph" width="15" height="15" viewBox="0 0 24 24" fill="none"
                             stroke="currentColor" stroke-width="1.9" stroke-linecap="round"
                             stroke-linejoin="round" aria-hidden="true">
                            <path d="M3 6.5h18v11H3z"/><path d="m3 7 9 6 9-6"/>
                        </svg>
                        Email preferences
                    </p>

                    <!-- THREE rows, not two, and they are in ascending order of how
                         much say the reader has. Two rows forced guidance and marketing
                         to share one switch, so a customer who had never been asked
                         about marketing could not be told how to use what they were
                         paying for. See productGuidance.js for the bases. -->
                    <div class="pref-grid">
                        <!-- Shown, and deliberately not switchable. A single "emails"
                             toggle invites someone to turn it off and then wonder why a
                             receipt arrived -- or to turn it off expecting receipts to
                             stop and miss a failed payment. Naming the channels makes
                             the distinction visible at the moment someone is thinking
                             about it, and it is the distinction the law cares about. -->
                        <div class="pref-row">
                            <div class="pref-copy">
                                <p class="pref-title">Account and billing</p>
                                <p class="pref-sub">Receipts, renewals, invitations and security notices.</p>
                            </div>
                            <span class="pref-always">Always on</span>
                        </div>

                        <!-- Legitimate interest with a right to object, so it is ON
                             until someone says otherwise. Absence is not an unanswered
                             question here. -->
                        <div class="pref-row">
                            <div class="pref-copy">
                                <p class="pref-title">Using Cuelist</p>
                                <p class="pref-sub">How to use the features you already have.</p>
                            </div>
                            <label class="pref-switch" :class="{ on: guidanceOn, busy: guidanceSaving }">
                                <input type="checkbox" v-model="guidanceOn" @change="saveGuidance" :disabled="guidanceSaving" />
                                <span class="knob"></span>
                            </label>
                        </div>

                        <!-- Consent, so absence is a QUESTION and must not render as a
                             switch already sitting on "off". 5,369 accounts have no
                             record in any system and never will until they answer; a
                             definite "off" would tell every one of them something we do
                             not know, and tell the ones already on the mailing list
                             something that is false. -->
                        <div class="pref-row">
                            <div class="pref-copy">
                                <p class="pref-title">Product news and offers</p>
                                <p class="pref-sub">Our newsletter as well as promotions and upgrade offers.</p>
                            </div>
                            <p class="pref-unset" v-if="!marketingConsentAnswered">You haven't chosen yet.</p>
                            <div class="pref-choice" v-if="!marketingConsentAnswered">
                                <button type="button" class="pref-ans" :disabled="consentSaving" @click="answerMarketingConsent(true)">Yes</button>
                                <button type="button" class="pref-ans ghost" :disabled="consentSaving" @click="answerMarketingConsent(false)">No thanks</button>
                            </div>
                            <label v-else class="pref-switch" :class="{ on: marketingConsent, busy: consentSaving }">
                                <input type="checkbox" v-model="marketingConsent" @change="saveMarketingConsent" :disabled="consentSaving" />
                                <span class="knob"></span>
                            </label>
                        </div>
                    </div>
                </section>

                <!-- The Notes List — full-width band beneath the top zone.
                     Account-level connect/disconnect ("sync") only, ported from
                     feature/lxnotes-project-link. Buying the joint ("dual") plan stays
                     in the upgrade-account modal, per the devs' design. Shown only when
                     the integration env flag is on. -->
                <section class="card nl-band" v-if="notesListIntegrationEnabled">
                    <div class="nl-band-main">
                        <div class="nl-head">
                            <p class="card-eyebrow">
                                <!-- Horizontal link, not the Notes List brand mark. Their logo is
                                     already in the app (cue-header.vue) but it is drawn as a logo:
                                     at 15px the flourish blurs, and it carries a WHITE background
                                     rect that becomes a lit card on the dark theme. The band names
                                     the partner in their purple already. -->
                                <svg class="glyph" width="15" height="15" viewBox="0 0 24 24" fill="none"
                                     stroke="currentColor" stroke-width="1.9" stroke-linecap="round"
                                     stroke-linejoin="round" aria-hidden="true">
                                    <path d="M9.5 17H7.5A5 5 0 0 1 7.5 7H9.5"/>
                                    <path d="M14.5 7H16.5A5 5 0 0 1 16.5 17H14.5"/>
                                    <path d="M8.5 12H15.5"/>
                                </svg>
                                <span class="nl-name">The <span class="nl-accent">Notes</span> List</span>
                            </p>
                            <span class="nl-badge" :class="notesListAccountStatus.linkStatus === 'linked' ? 'is-linked' : 'is-disconnected'">
                                <span class="nl-badge-dot"></span>
                                {{ notesListAccountStatus.linkStatus === 'linked' ? 'Connected' : 'Not connected' }}
                            </span>
                            <!-- The action lives in the header row, beside the status badge.
                                 "Not connected" and "Connect account" are the same thought
                                 stated twice, so keeping them on one line lets the state and
                                 its remedy be read together. It also fixes the dead corner the
                                 old two-column split created: the copy wraps at 70ch and never
                                 reached the button, so whichever corner held it, the opposite
                                 one was empty. -->
                            <div class="nl-band-action">
                                <button v-if="notesListAccountStatus.linkStatus !== 'linked'" class="btn btn-primary" :disabled="notesListAccountWorking" @click="connectNotesListAccount">
                                    {{ notesListAccountWorking ? 'Connecting…' : 'Connect account' }}
                                </button>
                                <button v-else class="btn btn-ghost" :disabled="notesListAccountWorking" @click="disconnectNotesListAccount">
                                    {{ notesListAccountWorking ? 'Working…' : 'Disconnect account' }}
                                </button>
                            </div>
                        </div>
                        <p class="nl-copy" v-if="notesListAccountStatus.linkStatus === 'linked'">
                            Your Cuelist and Notes List accounts are linked.
                        </p>
                        <p class="nl-copy" v-else>
                            Link your Notes List account to sync your work across both sites. Structure sync requires <strong>Cuelist + Notes List access</strong>.
                        </p>
                        <!-- Full-width rule, carried over from production. It does two jobs:
                             separates "what this is" from "what to do next", and declares the
                             card's edges so the width reads as deliberate rather than being
                             inferred from wherever the 70ch copy happens to stop. -->
                        <div class="nl-sep" role="separator"></div>
                        <!-- Connecting the ACCOUNT is only step one — each project is linked
                             separately from its own Settings. Without saying so, someone who
                             connects here sees nothing happen and has no idea what to do next.
                             Wording carried over from staging-v2-main, which shows the same two
                             messages (an amber warning bar when unlinked, a hint when linked);
                             the copy is theirs, the styling is this band's. -->
                        <p class="nl-hint" v-if="notesListAccountStatus.linkStatus === 'linked'">
                            Link a project to a production from that project's own Settings menu
                            (open the project, then Settings &gt; The Notes List).
                        </p>
                        <p class="nl-hint nl-hint--todo" v-else>
                            Connect your Notes List account, then link each production from that
                            project's own Settings menu.
                        </p>
                    </div>
                </section>
            </section>

            <!-- ============ USER SESSIONS ============ -->
            <section class="acct-panel" v-show="activeTab === 'sessions'">
                <div class="sessions">
                    <div class="sessions-head">
                        <div>
                            <h1 class="sessions-title">Active sessions</h1>
                            <p class="sessions-sub">Devices currently signed in to your Cuelist account.</p>
                        </div>
                        <span class="count-pill" v-if="liveSessions.length">{{ liveSessions.length }} of 3 active</span>
                    </div>

                    <div class="sessions-note">
                        <span class="note-ico">i</span>
                        <p>You can stay signed in on up to <strong>3 devices</strong> at once. Signing in on a new device beyond that automatically signs out your oldest session.</p>
                    </div>

                    <div class="session-list" v-if="liveSessions.length > 0">
                        <div class="session-item card" :class="{ 'is-current': session.isCurrent }" v-for="(session, index) in liveSessions" :key="index">
                            <div class="dev-ico">
                                <svg v-if="session.deviceType === 'Tablet'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="2.5" width="14" height="19" rx="2"/><path d="M11 18.5h2"/></svg>
                                <svg v-else-if="session.deviceType === 'Mobile'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="7" y="2.5" width="10" height="19" rx="2"/><path d="M11 18.5h2"/></svg>
                                <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="2.5" y="4" width="19" height="12.5" rx="1.5"/><path d="M9 20h6M12 16.5V20"/></svg>
                            </div>
                            <div class="dev-info">
                                <p class="dev-name">{{ session.device }}<span v-if="session.deviceVendorModel"> · {{ session.deviceVendorModel }}</span> <span v-if="session.isCurrent" class="cur-pill">This device</span></p>
                                <p class="dev-meta">{{ session.os }} · {{ session.ip }} · {{ session.lastActive }}</p>
                            </div>
                            <template v-if="!session.isCurrent">
                                <div v-if="loadingSessionId === session.sessionId" class="spinner"></div>
                                <button v-else class="btn btn-signout" @click="confirmLogout(session.sessionId)">Sign out</button>
                            </template>
                        </div>
                    </div>

                    <div class="sessions-loading" v-else>
                        <div class="spinner"></div>
                        <span>Loading your active sessions…</span>
                    </div>
                </div>

                <md-dialog-confirm
                    :md-title="confirm.title"
                    :md-content-html="getConfirmContent()"
                    :md-ok-text="confirm.ok"
                    :md-cancel-text="confirm.cancel"
                    @close="onDialogClose"
                    ref="logoutSessionDialog">
                </md-dialog-confirm>
            </section>

            <!-- Build stamp. Chrome, not account data, so it sits below everything rather
                 than in the rail (which is full) or on the Subscription card (whose upper
                 right means *price*). Click copies the full diagnostic, environment
                 included; the visible string deliberately does not show it. -->
            <p class="acct-build">
                <button type="button" class="acct-build-btn" @click="copyBuildStamp" :title="buildCopied ? 'Copied' : 'Click to copy build details'">
                    {{ buildCopied ? 'Copied to clipboard' : buildStampText }}
                </button>
            </p>

        </main>

        <core-footer></core-footer>

        <!-- Change-password modal (email users) -->
        <div class="acct-modal-overlay" v-if="passwordModalOpen" @click.self="closePasswordModal">
            <div class="acct-modal" role="dialog" aria-modal="true" aria-label="Change password">
                <div class="acct-modal-head">
                    <h2>Change password</h2>
                    <button class="acct-modal-x" @click="closePasswordModal" aria-label="Close">✕</button>
                </div>
                <p class="acct-modal-sub">For your Cuelist email login.</p>
                <change-password @close="closePasswordModal" @success="closePasswordModal"></change-password>
            </div>
        </div>

        <upgrade-account ref="upgradeAcct"></upgrade-account>
        <affiliate-institution ref="affiliateInstitution" v-if="!user.active_pay || (user.active_pay && user.affiliateInstitution)"></affiliate-institution>
        <manage-affiliation ref="manageAffiliation"  v-if="user.active_pay && (user.subscription_level == 'educational' || user.subscription_level == 'institutional' || user.subscription_level == 'dual_educational' || user.subscription_level == 'dual_institution')"></manage-affiliation>
        <billing-history ref="billingHistory" v-if="isPaidCustomer && user.stripe_id"></billing-history>
        <reauthenticate ref="reauthenticate" @close="onAuthEnd"></reauthenticate>
        <maintenance-notice ref="maintenanceNotice"></maintenance-notice>
    </div>
</template>

<script>
import firebase from 'firebase/app';
import { mapGetters } from 'vuex';
import { db } from '@/main';
import repos from '@/data';

import Vue from 'vue';

import NavBus from '@/buses/nav';
import CoreNavMain from '@/components/core/nav/main';
import CoreFooter from '@/components/core/footer';
import ProfilePhoto from '@/components/account/profile-photo';
import ChangePassword from '@/components/account/change-password';
import UpgradeAccount from '@/modals/upgrade-account';
import AffiliateInstitution from '@/modals/affiliate-institution';
import ManageAffiliation from '@/modals/manage-affiliation';
import PendingAffiliations from '@/components/dashboard/pending-affiliations';
import { consentFields } from '@/lib/marketingConsent';
import { guidanceFields, receivesGuidance } from '@/lib/productGuidance';
import BillingHistory from '@/modals/billing-history';
import Reauthenticate from '@/modals/reauthenticate';
import MaintenanceNotice from '@/modals/maintenance-notice';
import NoteslistPlans from '@/modals/noteslist-plans';
import { accountCardStudent, cancellingLine } from '@/lib/student-plan';
import noteslistMarkDark from '@/assets/img/noteslist-mark.png';
import noteslistMarkLight from '@/assets/img/noteslist-mark-light.png';

import { UPDATE_EMAIL_URL, NOTESLIST_INTEGRATION_ENABLED, CLOUD_FUNCTIONS_BASE } from '@/lib/URLs';
import { readPendingPlanUpdate, clearPendingPlanUpdate as clearPendingPlanUpdateMarker } from '@/lib/pending-plan-update';
import { buildStamp, buildDiagnostic } from '@/lib/buildStamp';
import { renameUpdate } from '@/lib/user-name';

const AccountView = {
    name: 'account',
    data: function() {
        return {
            buildCopied: false,
            // Mirrors user.marketing_consent. Held in data rather than computed
            // because a computed bound to v-model would fight the optimistic
            // toggle in saveMarketingConsent().
            marketingConsent: false,
            consentSaving: false,
            // Mirrors the INVERSE of user.product_guidance_opt_out: the switch
            // reads "receiving", the field records "objected". Absence means
            // receiving, so this starts true.
            guidanceOn: true,
            guidanceSaving: false,
            // Set by the affiliateInvitations binding in beforeRouteEnter. The
            // panel stays hidden until the listener has actually reported, so a
            // slow first snapshot shows nothing rather than an empty heading.
            affiliateInvitesReady: false,
            editingName: false,
            editingEmail: false,
            new_username: '',
            new_email: '',
            theme: 'default',
            ready: false,
            doing: false,
            handler: 0,
            activeTab: 'profile',
            passwordModalOpen: false,
            confirm: {
                title: 'Logout Session',
                ok: 'Logout',
                cancel: 'Cancel'
            },
            isLogoutDialogOpen: false,
            selectedSessionId: null,
            liveSessions: [],
            loadingSessionId: null,
            affiliateInstitutionName: '',
            // --- The Notes List integration (ported from feature/lxnotes-project-link) ---
            // Account-level connect/disconnect ("sync"). Flag-driven, same as staging,
            // so this file promotes outward unchanged. Sandbox turns the band on by
            // setting VUE_APP_NOTESLIST_INTEGRATION_ENABLED=true in deploy-sandbox.yml;
            // the dev switcher below drives the states because sandbox has no partner
            // credentials.
            notesListIntegrationEnabled: NOTESLIST_INTEGRATION_ENABLED,
            notesListAccountStatus: { linkStatus: 'disconnected' },
            notesListAccountWorking: false,
            notesListStatusLoading: false,
            planUpdatePending: false,
            planUpdateSlow: false,
            pendingPlanSnapshot: null
        };
    },
    computed: {
        // Student plan line on the Subscription card (Josh, 15 Oct 2026: card mock option 1, badge dropped).
        // While a cancel is set, every plan names itself and its end date (Josh, 15 Oct 2026).
        cancellingCard() {
            return cancellingLine(this.user, new Date(), {
                month: process.env.VUE_APP_STRIPE_STUDENT_MONTHLY_PLANID || '',
                year: process.env.VUE_APP_STRIPE_STUDENT_YEARLY_PLANID || ''
            });
        },
        studentCard() {
            return accountCardStudent(this.user, new Date(), {
                month: process.env.VUE_APP_STRIPE_STUDENT_MONTHLY_PLANID || '',
                year: process.env.VUE_APP_STRIPE_STUDENT_YEARLY_PLANID || ''
            });
        },
        // ABSENCE is a question, not an answer -- the same distinction
        // dashboard.vue:258 gates its prompt on. `false` means they declined and
        // the switch shows off; undefined means nobody has asked, and the row
        // offers the question instead of asserting a preference on their behalf.
        marketingConsentAnswered() {
            return !!(this.user && this.user.marketing_consent !== undefined);
        },
        buildStampText() {
            return buildStamp();
        },
        ...mapGetters([
            'user',
            'userByUID',
            'showMode',
            'isPaidCustomer'
        ]),
        isEmailUser() {
            return !!(this.user && this.user.signInMethod === 'email');
        },
        // An affiliation the user has SET but the institution has not activated.
        // `updateUserAffiliate` writes affiliateInstitution alone; active_pay is
        // cascaded by the CF only once the admin puts them in an active slot. So
        // for a non-subscriber, an affiliation on the doc is by definition not
        // yet granting anything.
        affiliationPending() {
            return !!(this.user && this.user.affiliateInstitution && !this.user.active_pay);
        },
        // affiliateInstitutionName reads institution_name off the ADMIN's user
        // doc, which can legitimately be blank. Falling back keeps the sentence
        // from rendering as "Affiliated with ."
        affiliateInstitutionLabel() {
            return this.affiliateInstitutionName || 'your institution';
        },
        isInstitutionAdmin() {
            return !!(this.user && this.user.active_pay
                && (this.user.subscription_level === 'educational' || this.user.subscription_level === 'institutional'));
        },
        // Google profile photos come back at 96px (…=s96-c); request a larger
        // size so the large account avatar isn't upscaled and soft. Uploaded
        // (email-user) photos are left untouched.
        avatarUrl() {
            const url = this.user && this.user.profile_picture;
            if (!url) return '';
            if (/googleusercontent\.com/.test(url)) {
                return /=s\d+-c/.test(url) ? url.replace(/=s\d+-c/, '=s400-c') : url + '=s400-c';
            }
            return url;
        },
        isNameComplete() {
            return this.new_username !== '';
        },
        isEmailComplete() {
            return this.new_email !== '';
        },
        actionUrl() {
            return `${CLOUD_FUNCTIONS_BASE}/create-portal-session`;
        },
        checkoutUrl() {
            return `https://us-central1-${process.env.VUE_APP_FIREBASE_PROJECTID}.cloudfunctions.net/app/create-checkout-session`;
        },
        // One-time Show Pass price (test/live id supplied via frontend env). The
        // CTA is hidden until this is configured.
        showPassPriceId() {
            return process.env.VUE_APP_STRIPE_SHOWPASSID || '';
        },
        eduInstAdminLabel() {
            const level = this.user && this.user.subscription_level;
            if (level === 'dual_educational') return 'Dual Educational Subscription Administrator';
            if (level === 'dual_institution') return 'Dual Institutional Subscription Administrator';
            if (level === 'educational') return 'Educational Subscription Administrator';
            return 'Institutional Subscription Administrator';
        },
        isNotesListAccountLinked() {
            return this.notesListAccountStatus.linkStatus === 'linked';
        },
        noteslistLogoSrc() {
            return this.showMode ? noteslistMarkDark : noteslistMarkLight;
        }
    },
    watch: {
        // ⛔ These two MUST be watchers, not a read in mounted().
        //
        // `user` comes from Firestore and lands AFTER mount. Seeding the switches
        // in mounted() read an empty object on every browser refresh and set both
        // from nothing — `marketingConsent` became false while the record said
        // true, and because `marketingConsentAnswered` is a COMPUTED it correctly
        // flipped to showing a switch, so the row rendered a confident "off" over
        // a record that said otherwise. Reported after the 13 Sept release; it
        // predates that work and was present on staging too.
        //
        // Guidance hid the same bug: receivesGuidance(null) is true and the data
        // default is true, so an empty user happened to look right — but anyone
        // who had opted OUT saw their switch come back on, which is the worse
        // direction to be wrong in.
        //
        // Watching the exact fields, not `user`, so nothing fires for unrelated
        // account changes. Absent stays absent: if the field never appears the
        // handler never runs and the data default stands, which is what keeps the
        // unanswered state working for the 5,973 accounts that have no record.
        //
        // The saving guards stop a stale value stomping an optimistic toggle
        // mid-write.
        'user.marketing_consent': {
            immediate: true,
            handler(val) {
                if (!this.consentSaving) this.marketingConsent = val === true;
            }
        },
        'user.product_guidance_opt_out': {
            immediate: true,
            handler() {
                // receivesGuidance(), not `val !== true` — absence-means-receiving
                // is the rule and it lives in productGuidance.js. Kept in one place.
                if (!this.guidanceSaving) this.guidanceOn = receivesGuidance(this.user);
            }
        },
        showMode: {
            immediate: true,
            handler: function(newVal) {
                this.theme = newVal ? 'show-mode' : 'default';
                Vue.material.setCurrentTheme(this.theme);
            }
        },
        'user.affiliateInstitution': {
            immediate: true,
            async handler(newVal) {
                if (newVal) {
                    const affiliateUser = await this.userByUID(newVal);
                    this.affiliateInstitutionName = (affiliateUser && affiliateUser.institution_name) || '';
                } else {
                    this.affiliateInstitutionName = '';
                }
            }
        },
        user: {
            deep: true,
            handler(newVal) {
                if (!this.planUpdatePending || !this.pendingPlanSnapshot || !newVal) {
                    return;
                }
                const snap = this.pendingPlanSnapshot;
                const changed =
                    !!newVal.active_pay !== snap.prevActivePay ||
                    (newVal.subscription_id || '') !== (snap.prevSubscriptionId || '') ||
                    (newVal.price_id || '') !== (snap.prevPriceId || '');
                if (changed) {
                    this.clearPendingPlanUpdate();
                }
            }
        }
    },
    methods: {
        async saveMarketingConsent() {
            // Optimistic: the switch has already moved. On failure put it back,
            // rather than leave the UI asserting something the record does not
            // say -- a preference that silently fails to save is worse than one
            // that visibly refuses.
            const desired = this.marketingConsent;
            this.consentSaving = true;
            try {
                await repos.users.update(this.user.uid,
                    consentFields(desired, 'account_settings'));
            } catch (e) {
                console.error('marketing consent save failed:', e.message);
                this.marketingConsent = !desired;
            } finally {
                this.consentSaving = false;
            }
        },
        // First answer from the account page. Writes the same record the
        // dashboard prompt does, so from here on the row is an ordinary switch.
        async answerMarketingConsent(granted) {
            this.consentSaving = true;
            try {
                await repos.users.update(this.user.uid,
                    consentFields(granted, 'account_settings'));
                this.marketingConsent = granted;
            } catch (e) {
                console.error('marketing consent save failed:', e.message);
                if (this.$toast) this.$toast.error('Could not save that. Please try again.');
            } finally {
                this.consentSaving = false;
            }
        },
        // The switch reads "receiving", the field records "objected", so the
        // value is inverted on the way in. Optimistic with the same rollback as
        // consent: a preference that silently fails to save is worse than one
        // that visibly refuses.
        async saveGuidance() {
            const desired = this.guidanceOn;
            this.guidanceSaving = true;
            try {
                await repos.users.update(this.user.uid,
                    guidanceFields(!desired, 'account_settings'));
            } catch (e) {
                console.error('guidance preference save failed:', e.message);
                this.guidanceOn = !desired;
            } finally {
                this.guidanceSaving = false;
            }
        },
        copyBuildStamp() {
            const text = buildDiagnostic('account');
            const done = () => {
                this.buildCopied = true;
                setTimeout(() => { this.buildCopied = false; }, 1800);
            };
            // navigator.clipboard needs a secure context and is missing on older Safari,
            // which is exactly the browser a support caller is most likely to be using.
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(text).then(done).catch(() => { this.fallbackCopy(text, done); });
            } else {
                this.fallbackCopy(text, done);
            }
        },
        fallbackCopy(text, done) {
            try {
                const el = document.createElement('textarea');
                el.value = text;
                el.setAttribute('readonly', '');
                el.style.position = 'fixed';
                el.style.opacity = '0';
                document.body.appendChild(el);
                el.select();
                document.execCommand('copy');
                document.body.removeChild(el);
                done();
            } catch (e) {
                console.warn('build stamp copy failed:', e.message);
            }
        },
        handleAffiliationAccepted() {
            // Same treatment as dashboard.vue: accepting affiliates the user and
            // the seat cascade runs server-side across two docs. A reload is the
            // honest way to show the result — the plan name, the badge and the
            // button row on this very card all derive from state that changes.
            window.location.reload(true);
        },
        openNav() {
            NavBus.$emit('main-nav.open');
        },
        goDashboard() {
            if (this.$route.name !== 'dashboard') {
                this.$router.push({ name: 'dashboard' });
            }
        },
        openPasswordModal() {
            this.passwordModalOpen = true;
        },
        closePasswordModal() {
            this.passwordModalOpen = false;
        },
        getConfirmContent() {
            return 'Are you sure you want to log out of the selected session?';
        },
        getTimeDifference(loginTime) {
            const now = new Date();
            const loginDate = new Date(loginTime);
            const diffInSeconds = Math.floor((now - loginDate) / 1000);

            if (diffInSeconds < 60) {
                return `${diffInSeconds} second${diffInSeconds === 1 ? '' : 's'} ago`;
            }

            const diffInMinutes = Math.floor(diffInSeconds / 60);
            if (diffInMinutes < 60) {
                return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`;
            }

            const diffInHours = Math.floor(diffInMinutes / 60);
            if (diffInHours < 24) {
                return `${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`;
            }

            const diffInDays = Math.floor(diffInHours / 24);
            return `${diffInDays} day${diffInDays === 1 ? '' : 's'} ago`;
        },
        toggleShowMode() {
            this.$store.dispatch('updateShowMode', !this.showMode);
            this.theme = !this.showMode ? 'show-mode' : 'default';
            Vue.material.setCurrentTheme(this.theme);
        },
        openDialog() {
            if (this.user.signInMethod === 'email') {
                this.$refs.imageUpload.open();
            }
        },
        editName() {
            if (this.user.signInMethod === 'email') {
                this.editingName = true;
                this.new_username = this.user.username;
            }
        },
        saveName() {
            if (this.new_username !== '') {
                // One name field until the redesign splits it, so a rename
                // clears the stored first/last (readers fall back to the
                // last-word rule). Saving the same name keeps them.
                this.$store.dispatch('updateUser', {
                    uid: this.user.uid,
                    update: renameUpdate(this.new_username, this.user.username,
                        firebase.firestore.FieldValue.delete())
                });
            }
            this.editingName = false;
        },
        cancelEditingName() {
            this.editingName = false;
        },
        editEmail() {
            if (this.user.signInMethod === 'email') {
                this.editingEmail = true;
                this.new_email = this.user.email;
            }
        },
        async saveEmail() {
            if (this.new_email === this.user.email) {
                this.editingEmail = false;
                return;
            }
            if (this.new_email !== '') {
                this.$refs.reauthenticate.open();
            }
        },
        cancelEditingEmail() {
            this.editingEmail = false;
        },
        async onAuthEnd(status) {
            if (status) {
                const currentUser = firebase.auth().currentUser;
                try {
                    const promises = [
                        currentUser.updateEmail(this.new_email),
                        this.$store.dispatch('updateUser', {
                            uid: this.user.uid,
                            update: {
                                email: this.new_email
                            }
                        })
                    ];
                    if (this.user.stripe_id) {
                        promises.push(
                            Vue.http.post(UPDATE_EMAIL_URL, {
                                customer_id: this.user.stripe_id || null,
                                email: this.new_email
                            })
                        );
                    }
                    await Promise.all(promises);
                } catch (e) {
                    console.error(e);
                }
            }
            this.editingEmail = false;
        },
        upgradeSubscription() {
            this.$refs.upgradeAcct.open({ preferDual: true });
        },
        switchPlan() {
            this.$refs.upgradeAcct.open();
        },
        updateAccount() {
            this.$refs.upgradeAcct.open();
        },
        affiliateInstitution() {
            this.$refs.affiliateInstitution.open();
        },
        manageActiveUsers() {
            this.$refs.manageAffiliation.open();
        },
        openBillingHistory() {
            this.$refs.billingHistory.open();
        },
        async confirmLogout(sessionId) {
            this.selectedSessionId = sessionId;
            await this.$nextTick();
            await this.$refs.logoutSessionDialog.open();
        },
        async onDialogClose(type) {
            if (type === 'ok' && this.selectedSessionId) {
                await this.logoutSession(this.selectedSessionId);
            }
        },
        async logoutSession(sessionId) {
            this.loadingSessionId = sessionId;
            const sessionIndex = this.liveSessions.findIndex(session => session.sessionId === sessionId);
            if (sessionIndex !== -1) {
                const userId = firebase.auth().currentUser.uid;
                await repos.users.update(userId, {
                    [`userDeviceSession.${sessionId}`]: firebase.firestore.FieldValue.delete()
                });
                this.fetchLiveSessions();
            }
            this.loadingSessionId = null;
        },
        async currentUser() {
            const currentUser = firebase.auth().currentUser;
            const userDoc = await repos.users.get(currentUser.uid);
            const userData = userDoc.data();
            return userData;
        },
        async loadNotesListAccountStatus() {
            if (!this.notesListIntegrationEnabled) {
                return;
            }
            this.notesListStatusLoading = true;
            try {
                this.notesListAccountStatus = await this.$store.dispatch('fetchNotesListAccountStatus');
            } catch (error) {
                this.$toast.error(error.message || 'Failed to load integration status');
            } finally {
                this.notesListStatusLoading = false;
            }
        },
        openNotesListPlans() {
            if (this.$refs.noteslistPlans) {
                this.$refs.noteslistPlans.open();
            }
        },
        async onNotesListPlansUpdated() {
            await this.loadNotesListAccountStatus();
        },
        async connectNotesListAccount() {
            this.notesListAccountWorking = true;
            try {
                const result = await this.$store.dispatch('startNotesListAccountLink');
                if (result.connectUrl) {
                    window.location.href = result.connectUrl;
                }
            } catch (error) {
                this.$toast.error(error.message || 'Failed to start account connection');
            } finally {
                this.notesListAccountWorking = false;
            }
        },
        async disconnectNotesListAccount() {
            this.notesListAccountWorking = true;
            try {
                await this.$store.dispatch('unlinkNotesListAccount');
                this.notesListAccountStatus = { linkStatus: 'disconnected' };
                this.$toast.success('Notes List account disconnected');
            } catch (error) {
                this.$toast.error(error.message || 'Failed to disconnect account');
            } finally {
                this.notesListAccountWorking = false;
            }
        },
        checkPendingPlanUpdate() {
            const marker = readPendingPlanUpdate();
            if (!marker) {
                return;
            }
            this.pendingPlanSnapshot = marker;
            this.planUpdatePending = true;
            this.planUpdateSlow = false;
            this.planUpdateSlowTimer = setTimeout(() => {
                this.planUpdateSlow = true;
            }, 2000);
            // Failsafe: don't hold the overlay for Stripe webhook lag (often 5–6s).
            // Account still updates live from Firestore after this closes.
            this.planUpdateFailsafeTimer = setTimeout(() => {
                this.clearPendingPlanUpdate();
            }, 3000);
        },
        clearPendingPlanUpdate() {
            this.planUpdatePending = false;
            this.planUpdateSlow = false;
            this.pendingPlanSnapshot = null;
            clearTimeout(this.planUpdateSlowTimer);
            clearTimeout(this.planUpdateFailsafeTimer);
            clearPendingPlanUpdateMarker();
        },
        async fetchLiveSessions() {
            const userData = await this.currentUser();
            const userDeviceSessionData = userData.userDeviceSession;

            if (!userDeviceSessionData || typeof userDeviceSessionData !== 'object') {
                this.liveSessions = [];
                await firebase.auth().signOut();
                localStorage.removeItem('sessionID');
                this.$router.replace('/login');
                return;
            }

            const currentSessionID = localStorage.getItem('sessionID');

            this.liveSessions = Object.entries(userDeviceSessionData)
                .map(([sessionId, sessionData]) => ({
                    device: sessionData.deviceType === 'Tablet' || sessionData.deviceType === 'Mobile'
                        ? sessionData.browser.replaceAll('Mobile ', '')
                        : sessionData.browser,
                    deviceType: sessionData.deviceType,
                    deviceVendorModel: sessionData.deviceVendorModel,
                    os: sessionData.os,
                    ip: sessionData.ip,
                    lastActive: this.getTimeDifference(sessionData.loginTime),
                    isCurrent: sessionId === currentSessionID,
                    sessionId: sessionId
                }));
        }
    },
    mounted() {
        // The two switches are NOT seeded here. `user` is bound from Firestore
        // asynchronously, so on a browser refresh mounted() runs before it
        // arrives and both would be set from an empty object. Watchers below do
        // it instead. See the comment there.
        this.loadNotesListAccountStatus();
        this.fetchLiveSessions();
        this.loadNotesListAccountStatus();
        this.checkPendingPlanUpdate();
        this.sessionInterval = setInterval(() => {
            this.fetchLiveSessions();
        }, 1000);
    },
    beforeDestroy() {
        clearInterval(this.sessionInterval);
        clearTimeout(this.planUpdateSlowTimer);
        clearTimeout(this.planUpdateFailsafeTimer);
    },
    components: {
        'core-nav-main': CoreNavMain,
        'core-footer': CoreFooter,
        'profile-photo': ProfilePhoto,
        'change-password': ChangePassword,
        'upgrade-account': UpgradeAccount,
        'affiliate-institution': AffiliateInstitution,
        'manage-affiliation': ManageAffiliation,
        'pending-affiliations': PendingAffiliations,
        'billing-history': BillingHistory,
        reauthenticate: Reauthenticate,
        'maintenance-notice': MaintenanceNotice,
        'noteslist-plans': NoteslistPlans
    },
    beforeRouteEnter: function(to, from, next) {
        next((vm) => {
            // The whole-users-collection binding (setUsersRef) was removed 23 Sept 2026:
            // its only reader was the institution picker, now a server-side search
            // (searchInstitutions). It streamed every user record into this page.
            vm.$store.dispatch('setActiveUser', {
                ref: db.collection('users').doc(firebase.auth().currentUser.uid),
                callback: () => { vm.ready = true; }
            });
            // ⚠ This binding is why the panel works on a DIRECT load of /account.
            // The same email-scoped listener is bound in dashboard.vue, and the
            // result lands in the shared store — so navigating dashboard → account
            // inside the app makes the panel appear whether or not this exists.
            // Without it, anyone who bookmarks /account or lands here from an
            // email link sees nothing at all, and it passes casual QA every time.
            // Not a duplicate listener: bindFirestoreRef auto-unbinds the previous
            // one on the same 'affiliateInvitations' slot (Store.js:198), and the two
            // views are never mounted at once. This is a rebind, not a second watch.
            vm.$store.dispatch('setAffiliateInvitationsRef', {
                ref: db.collection('affiliateInvitations')
                    .where('email', '==', (firebase.auth().currentUser.email || '').toLowerCase()),
                callback: () => { vm.affiliateInvitesReady = true; }
            });
        });
    }
};
export default AccountView;
</script>

<style lang="scss">
/* ============================================================
   Account page redesign — all styles namespaced under .acct2 so
   they don't leak into the Vue-material chrome (nav drawer, modals,
   footer). Dark mode is driven by .acct2.is-dark (bound to showMode),
   not prefers-color-scheme. No color-mix() — node-sass can't parse it.
   ============================================================ */
.acct2 {
    --header-bg: #0d2744;
    --header-fg: #eaf3f7;
    --accent: #73d5e3;
    --accent-ink: #12718a;
    --accent-tint: rgba(115,213,227,.16);
    --accent-tint-strong: rgba(115,213,227,.34);
    --accent-border: rgba(115,213,227,.55);
    --ring: 0 0 0 3px rgba(115,213,227,.55);

    --bg: #eef2f7;
    --surface: #ffffff;
    --surface-2: #f6f9fc;
    --border: #dde4ee;
    --border-strong: #c9d4e2;
    --text: #13263e;
    --text-muted: #5c6d84;

    --btn-primary-bg: #0d2744;
    --btn-primary-fg: #ffffff;
    --btn-primary-hover: #16375f;

    --pass-tint: #eefafc;
    --pass-border: #bfe8ef;

    --danger: #c0392b;
    --danger-tint: rgba(192,57,43,.10);
    --danger-border: rgba(192,57,43,.32);

    --shadow: 0 1px 2px rgba(13,39,68,.06), 0 8px 24px rgba(13,39,68,.06);

    background: var(--bg);
    color: var(--text);
    min-height: 100vh;
    font-family: system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}
.acct2.is-dark {
    --accent-ink: #73d5e3;
    --bg: #0a1421;
    --surface: #12243a;
    --surface-2: #0e1d30;
    --border: #24384f;
    --border-strong: #33506e;
    --text: #e9f1f8;
    --text-muted: #9fb4cb;
    --btn-primary-bg: #73d5e3;
    --btn-primary-fg: #08202c;
    --btn-primary-hover: #8fe0eb;
    --pass-tint: #102f38;
    --pass-border: #1f5563;
    --danger: #f0938c;
    --danger-tint: rgba(240,147,140,.12);
    --danger-border: rgba(240,147,140,.32);
    --shadow: 0 1px 2px rgba(0,0,0,.3), 0 10px 30px rgba(0,0,0,.35);
}

.plan-update-overlay {
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(13, 39, 68, 0.45);
    backdrop-filter: blur(2px);
    animation: plan-update-fade-in 0.15s ease-out;
}
.plan-update-card {
    width: 90%;
    max-width: 380px;
    padding: 32px 28px;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
    text-align: center;
}
.plan-update-spinner {
    width: 40px;
    height: 40px;
    margin: 0 auto 20px;
    border: 4px solid rgba(13, 39, 68, 0.12);
    border-top-color: #0d2744;
    border-radius: 50%;
    animation: noteslist-btn-spin 0.8s linear infinite;
}
.plan-update-title {
    font-size: 18px;
    font-weight: 600;
    color: #0d2744;
    margin-bottom: 10px;
}
.plan-update-subtitle {
    font-size: 14px;
    line-height: 1.5;
    color: rgba(13, 39, 68, 0.72);
    margin: 0;
}
.plan-update-dismiss {
    display: inline-block;
    margin-top: 18px;
    padding: 8px 18px;
    font-size: 14px;
    font-weight: 500;
    color: #0d2744;
    background: transparent;
    border: 1px solid rgba(13, 39, 68, 0.3);
    border-radius: 6px;
    cursor: pointer;
    transition: background 0.2s ease;
}
.plan-update-dismiss:hover {
    background: rgba(13, 39, 68, 0.06);
}
/* Ported from staging-v2-main: .plan-update-spinner animates with this and it
   lived in a style block sandbox never carried across. */
@keyframes noteslist-btn-spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}
@keyframes plan-update-fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
}
.md-theme-show-mode {
    .plan-update-card {
        background: #2c2c2c;
        box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
    }
    .plan-update-title {
        color: #fff;
    }
    .plan-update-subtitle {
        color: rgba(255, 255, 255, 0.75);
    }
    .plan-update-spinner {
        border-color: rgba(255, 255, 255, 0.18);
        border-top-color: #fff;
    }
    .plan-update-dismiss {
        color: #fff;
        border-color: rgba(255, 255, 255, 0.35);
    }
    .plan-update-dismiss:hover {
        background: rgba(255, 255, 255, 0.08);
    }
}
.container-upgradeAcct {
    /* overflow-x, NOT overflow. `overflow: hidden` here clipped the page
       VERTICALLY as well, and since this element is the account page's root the
       result was that anything past the first viewport could not be reached at
       all: no page scrollbar, no wheel, no keyboard. The band that happened to
       sit at the fold was simply sliced in half.
       It has been that way since the rule was added, and it has been shaping the
       page's design rather than being treated as the bug it is — see the
       .email-prefs comment below, where a band was rebuilt into two columns to
       keep the Notes List band above a fold that should never have existed.
       Horizontal clipping is kept, which is what the rule was for. */
    overflow-x: hidden;
}
.containerBox {
    display: flex;
    width: 100%;
    align-items: stretch;
    height: auto;
    min-height: 500px;
}

.acct2 * { box-sizing: border-box; }

/* ---- Header ---- */
.acct2 .acct-topbar {
    background: var(--header-bg);
    color: var(--header-fg);
    display: flex; align-items: center; justify-content: space-between;
    padding: 0 24px; height: 60px;
}
.acct2 .tb-left { display: flex; align-items: center; gap: 12px; }
.acct2 .tb-right { display: flex; align-items: center; }
.acct2 .tb-icon {
    background: transparent; border: none; color: var(--header-fg); cursor: pointer;
    padding: 6px; display: grid; place-items: center; border-radius: 8px;
}
.acct2 .tb-icon svg { width: 22px; height: 22px; }
.acct2 .tb-icon:hover { background: rgba(255,255,255,.1); }
.acct2 .tb-icon:focus-visible { outline: none; box-shadow: var(--ring); }
.acct2 .theme-toggle {
    display: flex; align-items: center; gap: 8px;
    background: rgba(255,255,255,.08); color: var(--header-fg);
    border: 1px solid rgba(255,255,255,.16); border-radius: 999px;
    padding: 6px 14px; font-size: 13px; cursor: pointer; font-weight: 600; font-family: inherit;
}
.acct2 .theme-toggle:hover { background: rgba(255,255,255,.16); }
.acct2 .theme-toggle:focus-visible { outline: none; box-shadow: var(--ring); }
.acct2 .brand-logo-link { display: inline-flex; align-items: center; cursor: pointer; border-radius: 6px; }
.acct2 .brand-logo-link:focus-visible { outline: none; box-shadow: var(--ring); }
.acct2 .brand-logo { height: 30px; width: auto; display: block; }

/* ---- Tabs ---- */
.acct2 .acct-tabs {
    background: var(--header-bg);
    display: flex; gap: 4px; padding: 0 24px;
    border-bottom: 1px solid rgba(255,255,255,.08);
}
.acct2 .acct-tab {
    background: transparent; border: none; border-bottom: 2px solid transparent;
    color: #93b0c4; font-size: 13px; font-weight: 600; letter-spacing: .08em;
    text-transform: uppercase; padding: 14px 16px 12px; cursor: pointer; font-family: inherit;
}
.acct2 .acct-tab:hover { color: var(--header-fg); }
.acct2 .acct-tab.is-active { color: #fff; border-bottom-color: var(--accent); }

/* ---- Layout ---- */
.acct2 .acct-build { text-align: center; margin: 28px 0 0; }
.acct2 .acct-build-btn {
    background: none; border: 0; padding: 4px 8px; cursor: pointer;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 11px; letter-spacing: .01em; color: var(--text-muted); opacity: .8;
}
.acct2 .acct-build-btn:hover { opacity: 1; text-decoration: underline; }
.acct2 .acct-content { max-width: 940px; margin: 0 auto; padding: 40px 24px 64px; }
.acct2 .acct-grid { display: grid; grid-template-columns: 300px 1fr; gap: 24px; align-items: stretch; }
@media (max-width: 760px) { .acct2 .acct-grid { grid-template-columns: 1fr; } }

.acct2 .card {
    background: var(--surface); border: 1px solid var(--border);
    border-radius: 14px; box-shadow: var(--shadow); padding: 24px;
}

/* ---- Identity rail ---- */
.acct2 .rail {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    text-align: center; padding: 28px 24px;
}
.acct2 .avatar-wrap { position: relative; width: 132px; margin: 0 auto 16px; }
.acct2 .avatar {
    width: 132px; height: 132px; border-radius: 50%; overflow: hidden;
    background: linear-gradient(150deg, #1d4e77, #0d2744 60%);
    box-shadow: 0 0 0 4px var(--surface), 0 0 0 6px var(--border-strong);
}
.acct2 .avatar img { width: 100%; height: 100%; object-fit: cover; display: block; }
.acct2 .avatar-edit {
    position: absolute; right: 6px; bottom: 6px; width: 34px; height: 34px; border-radius: 50%;
    background: var(--btn-primary-bg); color: var(--btn-primary-fg); border: 3px solid var(--surface);
    display: grid; place-items: center; cursor: pointer; font-size: 15px;
}
.acct2 .rail-name { font-size: 22px; font-weight: 700; margin: 0; letter-spacing: -.01em; }
.acct2 .rail-name.editable { cursor: pointer; }
.acct2 .rail-name.editable:hover { text-decoration: underline; text-decoration-color: var(--accent); text-underline-offset: 4px; }
.acct2 .rail-email { color: var(--text-muted); font-size: 14px; margin: 5px 0 0; word-break: break-word; }
.acct2 .rail-email.editable { cursor: pointer; }
.acct2 .rail-email.editable:hover { text-decoration: underline; text-decoration-color: var(--accent); text-underline-offset: 3px; }
.acct2 .rail-sync { color: var(--text-muted); font-size: 12px; margin: 10px 0 0; opacity: .85; }
.acct2 .rail-manage { display: inline-block; margin: 8px 0 0; font-size: 12px; font-weight: 600; color: var(--accent-ink); text-decoration: none; }
.acct2 .rail-manage:hover { text-decoration: underline; }

.acct2 .rail-edit { width: 100%; max-width: 240px; margin: 4px auto 0; }
.acct2 .rail-input {
    width: 100%; padding: 9px 12px; border-radius: 9px; border: 1px solid var(--border-strong);
    background: var(--surface-2); color: var(--text); font-size: 15px; font-family: inherit; text-align: center;
}
.acct2 .rail-input:focus-visible { outline: none; box-shadow: var(--ring); border-color: var(--accent); }
.acct2 .rail-edit-actions { display: flex; gap: 8px; justify-content: center; margin-top: 10px; }

.acct2 .status-pill {
    display: inline-flex; align-items: center; gap: 7px; margin: 18px auto 0; padding: 6px 14px;
    background: var(--surface-2); border: 1px solid var(--border); border-radius: 999px; font-size: 13px; font-weight: 600;
}
.acct2 .status-pill .dot { width: 8px; height: 8px; border-radius: 50%; background: var(--accent); }

.acct2 .rail-block { width: 100%; }
.acct2 .rail-div { height: 1px; background: var(--border); margin: 22px -24px 18px; }
.acct2 .rail-block-label { font-size: 11px; letter-spacing: .1em; text-transform: uppercase; color: var(--text-muted); margin: 0 0 4px; }
.acct2 .rail-block-value { font-size: 16px; font-weight: 650; margin: 0; }
.acct2 .rail-role { font-size: 13px; color: var(--text-muted); margin: 3px 0 0; }
.acct2 .rail-security {
    display: inline-flex; align-items: center; gap: 8px; background: transparent;
    border: 1px solid var(--border-strong); color: var(--text); border-radius: 9px;
    padding: 9px 16px; font-size: 13px; font-weight: 600; font-family: inherit; cursor: pointer;
}
.acct2 .rail-security:hover { background: var(--surface-2); }
.acct2 .rail-security:focus-visible { outline: none; box-shadow: var(--ring); }
.acct2 .rail-security .lock { font-size: 13px; }

/* ---- Content column: the two billing cards grow so their bottoms align with the rail ---- */
.acct2 .col { display: flex; flex-direction: column; gap: 24px; }
.acct2 .col > .card { flex: 1 1 auto; }
.acct2 .card-eyebrow {
    display: flex; align-items: center; gap: 9px; font-size: 12px; letter-spacing: .1em;
    text-transform: uppercase; color: var(--text-muted); font-weight: 700; margin: 0 0 16px;
}
.acct2 .card-eyebrow .glyph { font-size: 15px; }
/* SVG glyphs (Show Pass ticket) size themselves and must not be squeezed by the flex row. */
.acct2 .card-eyebrow svg.glyph { flex: none; }
.acct2 .plan-row { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; margin-bottom: 4px; }
.acct2 .plan-name { font-size: 20px; font-weight: 700; margin: 0; }
.acct2 .plan-badge {
    font-size: 11px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase;
    color: var(--accent-ink); background: var(--accent-tint); border: 1px solid var(--accent-tint-strong);
    padding: 3px 9px; border-radius: 6px;
}
.acct2 .plan-badge.muted { color: var(--text-muted); background: var(--surface-2); border-color: var(--border); }
.acct2 .plan-sub { color: var(--text-muted); font-size: 14px; margin: 0 0 20px; }
.acct2 .plan-sub strong { color: var(--text); font-weight: 650; }
.acct2 .plan-pending { color: var(--text); font-weight: 650; }

.acct2 .btn-row { display: flex; gap: 12px; flex-wrap: wrap; align-items: center; }
.acct2 .btn-row form { display: inline-flex; margin: 0; }
.acct2 .btn {
    appearance: none; border: 1px solid transparent; border-radius: 9px; padding: 11px 18px;
    font-size: 14px; font-weight: 650; cursor: pointer; font-family: inherit; letter-spacing: .01em;
}
.acct2 .btn:focus-visible { outline: none; box-shadow: var(--ring); }
.acct2 .btn-primary { background: var(--btn-primary-bg); color: var(--btn-primary-fg); }
.acct2 .btn-primary:hover { background: var(--btn-primary-hover); }
.acct2 .btn-ghost { background: transparent; color: var(--text); border-color: var(--border-strong); }
.acct2 .btn-ghost:hover { background: var(--surface-2); }

/* ---- Show Pass card ---- */
.acct2 .pass-card { background: var(--pass-tint); border-color: var(--pass-border); }
.acct2 .pass-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
.acct2 .pass-title { font-size: 20px; font-weight: 700; margin: 0 0 2px; }
.acct2 .price-chip {
    flex: none; text-align: center; background: var(--surface); border: 1px solid var(--pass-border);
    border-radius: 10px; padding: 8px 14px; font-variant-numeric: tabular-nums;
}
.acct2 .price-chip .amt { font-size: 20px; font-weight: 750; letter-spacing: -.01em; }
.acct2 .price-chip .per { display: block; font-size: 11px; color: var(--text-muted); text-transform: uppercase; letter-spacing: .06em; }
.acct2 .pass-copy { color: var(--text-muted); font-size: 14px; margin: 12px 0 20px; max-width: 46ch; }
.acct2 .pass-copy strong { color: var(--text); font-weight: 650; }

/* ---- Email preferences — full-width band, above the Notes List band ----
   TWO COLUMNS, not two stacked rows. Stacked, this band was tall enough to push
   the Notes List band below the fold on a fully-open browser, which defeats the
   point of putting Email preferences above it. Side by side it is roughly half
   the height and both bands sit in one screen. Padding is tightened against the
   standard .card for the same reason. */
.acct2 .email-prefs { margin-top: 20px; padding: 18px 24px; }
.acct2 .email-prefs .card-eyebrow { margin-bottom: 12px; }
/* THREE COLUMNS, and the reason is the fold rather than taste. Stacked, this
   band is ~240px tall and pushes the Notes List band off the first screen on a
   normal window. Scrolling works now, but needing to scroll to discover a card
   is not the same as it being visible. Side by side the band is ~90px and the
   whole page fits.
   Within each column the control sits BENEATH its copy, not beside it: at a
   third of the width there is no room for a sentence and a switch on one line,
   and the two-button unanswered state needs the full column. */
.acct2 .pref-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0 24px;
}
.acct2 .pref-row {
    display: flex; flex-direction: column; align-items: flex-start;
    gap: 10px; min-width: 0;
}
/* Divider between the columns rather than above each row — it separates the
   three channels, which is the distinction this band exists to draw. */
.acct2 .pref-row + .pref-row { padding-left: 24px; border-left: 1px solid var(--border); }
.acct2 .pref-copy { min-width: 0; }
.acct2 .pref-title { font-size: 14px; font-weight: 650; margin: 0; color: var(--text); }
.acct2 .pref-sub { font-size: 12.5px; color: var(--text-muted); margin: 2px 0 0; }
.acct2 .pref-always {
    flex: none; font-size: 10.5px; letter-spacing: .09em;
    text-transform: uppercase; color: var(--text-muted); white-space: nowrap;
}
/* Switch. Built from a real checkbox so it is keyboard reachable and announces
   its state; the input is visually hidden rather than display:none, which would
   take it out of the tab order entirely. */
.acct2 .pref-switch {
    flex: none; position: relative; width: 38px; height: 22px;
    border-radius: 999px; background: var(--border-strong); cursor: pointer;
    transition: background .16s;
}
.acct2 .pref-switch.on { background: var(--accent-ink); }
.acct2 .pref-switch.busy { opacity: .6; cursor: progress; }
.acct2 .pref-switch input {
    position: absolute; opacity: 0; width: 100%; height: 100%; margin: 0;
    cursor: inherit;
}
.acct2 .pref-switch .knob {
    position: absolute; top: 2.5px; left: 2.5px; width: 17px; height: 17px;
    border-radius: 50%; background: #fff; box-shadow: 0 1px 2px rgba(0,0,0,.25);
    transition: left .16s; pointer-events: none;
}
.acct2 .pref-switch.on .knob { left: 18.5px; }
.acct2 .pref-switch input:focus-visible ~ .knob { box-shadow: 0 0 0 3px var(--accent-border); }

/* The unanswered state. A question with two answers, not a switch sitting on a
   default — the same shape the dashboard prompt uses, and for the same reason:
   a switch is a thing you can ignore, and ignoring it would leave us asserting a
   preference nobody expressed. */
.acct2 .pref-unset {
    font-size: 12.5px; margin: -4px 0 0; color: var(--accent-ink); font-weight: 600;
}
.acct2 .pref-choice { flex: none; display: flex; gap: 8px; }
.acct2 .pref-ans {
    font: inherit; font-size: 12.5px; font-weight: 650; white-space: nowrap;
    padding: 6px 14px; border-radius: 999px; cursor: pointer;
    background: var(--btn-primary-bg); color: var(--btn-primary-fg);
    border: 1px solid transparent; transition: background .15s, border-color .15s;
}
.acct2 .pref-ans:hover:not(:disabled) { background: var(--btn-primary-hover); }
.acct2 .pref-ans.ghost {
    background: transparent; color: var(--text-muted); border-color: var(--border-strong);
}
.acct2 .pref-ans.ghost:hover:not(:disabled) { color: var(--text); border-color: var(--text-muted); }
.acct2 .pref-ans:disabled { opacity: .55; cursor: progress; }

/* Stack at the same breakpoint the main grid uses, so the page reflows as one. */
/* Three columns need more room than two did, so this stacks earlier than the
   main grid. Straight to one column: at two, the odd row out reads as though it
   belongs to neither. */
@media (max-width: 980px) {
    .acct2 .pref-grid { grid-template-columns: 1fr; gap: 0; }
    .acct2 .pref-row + .pref-row {
        padding-left: 0; border-left: 0;
        margin-top: 14px; padding-top: 14px; border-top: 1px solid var(--border);
    }
}

/* ---- The Notes List — full-width band beneath the top zone (ported integration) ---- */
.acct2 .nl-band {
    /* Single column now. The action sits in .nl-head, so the copy below runs the
       card's full width instead of being squeezed into a left column that never
       reached the button. */
    margin-top: 24px;
}
.acct2 .nl-band-main { min-width: 0; }
.acct2 .nl-band-action {
    flex: none;
    /* Pushed to the right end of the header row. .nl-head is a flex row whose
       other children hug the left, so auto margin claims all the slack. */
    margin-left: auto;
}
.acct2 .nl-head {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;

    /* The eyebrow carries a 16px bottom margin globally, which inside a centred
       flex row shifts the optical centre and leaves the badge and button sitting
       high. The row owns its own spacing instead. */
    .card-eyebrow { margin-bottom: 0; }
}
.acct2 .nl-head .card-eyebrow { margin: 0; }
.acct2 .nl-accent { color: #7B4FD1; }
.acct2.is-dark .nl-accent { color: #B38AF5; }
.acct2 .nl-badge {
    flex: none; display: inline-flex; align-items: center; gap: 6px; white-space: nowrap;
    font-size: 11px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase;
    padding: 4px 10px; border-radius: 999px; border: 1px solid transparent;
}
.acct2 .nl-badge-dot { width: 7px; height: 7px; border-radius: 50%; background: currentColor; }
.acct2 .nl-badge.is-linked { color: #2f855a; background: rgba(47,133,90,.12); border-color: rgba(47,133,90,.30); }
.acct2 .nl-badge.is-disconnected { color: #b7791f; background: rgba(183,121,31,.12); border-color: rgba(183,121,31,.30); }
.acct2.is-dark .nl-badge.is-linked { color: #7fcfa3; background: rgba(127,207,163,.14); border-color: rgba(127,207,163,.30); }
.acct2.is-dark .nl-badge.is-disconnected { color: #e6b980; background: rgba(230,185,128,.14); border-color: rgba(230,185,128,.30); }
.acct2 .nl-copy { color: var(--text-muted); font-size: 14px; margin: 12px 0 0; max-width: 70ch; }
.acct2 .nl-copy strong { color: var(--text); font-weight: 650; }
/* Guidance under the Notes List band: connecting the account is only step one,
   each project is linked from its own Settings. The --todo variant is the
   unlinked state, where staging uses an amber warning bar; kept as quiet
   accented text here to match this band rather than importing that treatment. */
.acct2 .nl-hint {
    color: var(--text-muted); font-size: 13px; line-height: 1.45; margin: 0;
    /* Deliberately NOT capped at 70ch like .nl-copy. It sits under the full-width
       rule and is what fills the bottom of the card; capping it would reopen the
       dead corner the rule is there to close. */
}
.acct2 .nl-sep {
    height: 1px;
    background: var(--border);
    margin: 16px 0;
}
.acct2 .nl-hint--todo { color: var(--accent-ink); }
@media (max-width: 760px) { .acct2 .nl-band { flex-direction: column; align-items: flex-start; gap: 18px; } }


/* ---- User sessions ---- */
.acct2 .sessions { max-width: 660px; margin: 0 auto; }
.acct2 .sessions-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 16px; }
.acct2 .sessions-title { font-size: 22px; font-weight: 700; margin: 0; letter-spacing: -.01em; }
.acct2 .sessions-sub { color: var(--text-muted); font-size: 14px; margin: 4px 0 0; }
.acct2 .count-pill {
    flex: none; background: var(--surface-2); border: 1px solid var(--border); border-radius: 999px;
    padding: 6px 14px; font-size: 13px; font-weight: 600; font-variant-numeric: tabular-nums; white-space: nowrap;
}
.acct2 .sessions-note {
    display: flex; gap: 11px; align-items: flex-start; background: var(--surface-2);
    border: 1px solid var(--border); border-radius: 12px; padding: 14px 16px; margin-bottom: 20px;
}
.acct2 .sessions-note .note-ico {
    flex: none; width: 20px; height: 20px; border-radius: 50%; display: grid; place-items: center;
    font-size: 12px; font-weight: 700; font-style: normal; background: var(--accent-tint); color: var(--accent-ink);
}
.acct2 .sessions-note p { margin: 0; font-size: 13.5px; color: var(--text-muted); line-height: 1.5; }
.acct2 .sessions-note strong { color: var(--text); font-weight: 650; }
.acct2 .session-list { display: flex; flex-direction: column; gap: 12px; }
.acct2 .session-item { display: flex; align-items: center; gap: 16px; padding: 15px 18px; }
.acct2 .session-item.is-current { border-color: var(--accent-border); }
.acct2 .dev-ico {
    flex: none; width: 44px; height: 44px; border-radius: 10px; background: var(--surface-2);
    border: 1px solid var(--border); display: grid; place-items: center; color: var(--text-muted);
}
.acct2 .dev-ico svg { width: 22px; height: 22px; }
.acct2 .session-item.is-current .dev-ico { color: var(--accent-ink); border-color: var(--accent-border); }
.acct2 .dev-info { flex: 1; min-width: 0; }
.acct2 .dev-name { margin: 0; font-weight: 650; font-size: 15px; display: flex; align-items: center; gap: 9px; flex-wrap: wrap; }
.acct2 .dev-meta { margin: 3px 0 0; color: var(--text-muted); font-size: 13px; font-variant-numeric: tabular-nums; }
.acct2 .cur-pill {
    font-size: 11px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase;
    color: var(--accent-ink); background: var(--accent-tint); border: 1px solid var(--accent-tint-strong);
    padding: 2px 8px; border-radius: 6px;
}
.acct2 .btn-signout { flex: none; color: var(--danger); border-color: var(--danger-border); background: transparent; }
.acct2 .btn-signout:hover { background: var(--danger-tint); }
.acct2 .sessions-loading { display: flex; flex-direction: column; align-items: center; gap: 14px; color: var(--text-muted); padding: 48px 0; }

/* ---- Change-password modal ---- */
.acct2 .acct-modal-overlay {
    position: fixed; inset: 0; background: rgba(6,15,26,.55);
    display: flex; align-items: center; justify-content: center; z-index: 60; padding: 20px;
}
.acct2 .acct-modal {
    background: var(--surface); border: 1px solid var(--border); border-radius: 16px;
    box-shadow: var(--shadow); width: 100%; max-width: 420px; padding: 24px 26px 26px;
}
.acct2 .acct-modal-head { display: flex; align-items: center; justify-content: space-between; }
.acct2 .acct-modal-head h2 { margin: 0; font-size: 19px; }
.acct2 .acct-modal-x {
    background: transparent; border: none; color: var(--text-muted); cursor: pointer;
    font-size: 16px; padding: 4px 6px; border-radius: 6px; line-height: 1;
}
.acct2 .acct-modal-x:hover { background: var(--surface-2); color: var(--text); }
.acct2 .acct-modal-sub { margin: 4px 0 16px; color: var(--text-muted); font-size: 13px; }

/* ---- Spinner (sessions) ---- */
.acct2 .spinner {
    width: 28px; height: 28px; border: 4px solid rgba(0,0,0,0.1);
    border-top-color: var(--accent); border-radius: 50%; animation: acct-spin 1s linear infinite;
}
.acct2.is-dark .spinner { border-color: rgba(255,255,255,0.12); border-top-color: var(--accent); }
@keyframes acct-spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }

@media (prefers-reduced-motion: reduce) { .acct2 * { transition: none !important; animation: none !important; } }

/* Keep the logout confirm dialog tidy (Vue-material dialog, still used). */
.md-dialog-container .md-dialog { overflow: hidden !important; }
</style>


// WEBPACK FOOTER //
// account.vue?4d159c24